import React from 'react';

/**
 * CustomButtonProps interface extends basic HTML button attributes and adds custom props
 * @interface CustomButtonProps
 * @extends {React.ButtonHTMLAttributes<HTMLButtonElement>}
 */
interface CustomButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Style variant of the button - determines color scheme and appearance */
  variant?: 'primary' | 'secondary' | 'danger' | 'cancel' | 'transparent';
  
  /** Size variant of the button - determines padding and font size */
  size?: 'sm' | 'md' | 'lg';
  
  /** Loading state - shows spinner and disables button when true */
  isLoading?: boolean;
  
  /** Icon to be displayed before the button text */
  leftIcon?: React.ReactNode;
  
  /** Icon to be displayed after the button text */
  rightIcon?: React.ReactNode;
}

/**
 * Custom button component with multiple variants, sizes, and icon support
 * @component
 * @param {CustomButtonProps} props - Component props
 * @returns {React.ReactElement} Rendered button element
 */
const CustomButton: React.FC<CustomButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  ...props
}) => {
  // Base styles applied to all button variants
  const baseStyles = "inline-flex items-center justify-center font-medium rounded-md focus:outline-none transition-colors duration-200";
  
  // Style variants for different button types
  const variants = {
    /** Primary button - Used for main actions */
    primary: "text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 disabled:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 dark:disabled:bg-blue-400",
    
    /** Secondary button - Used for secondary actions */
    secondary: "text-gray-900 bg-white border border-gray-200 hover:bg-gray-100 focus:ring-4 focus:ring-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white dark:disabled:bg-gray-700 dark:disabled:text-gray-500",
    
    /** Danger button - Used for destructive actions */
    danger: "text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 disabled:bg-red-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800 dark:disabled:bg-red-400",

    /** Cancel button - Used for cancel actions with custom styling */
    cancel: "bg-gray-400 hover:bg-gray-500 text-white focus:ring-4 focus:outline-none focus:ring-gray-300 border border-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600 dark:disabled:bg-gray-600 dark:disabled:text-gray-400",

    /** Transparent button with border */
    transparent: "px-5 py-2.5 rounded-lg border border-blue-500 hover:border-white hover:bg-blue-700 text-blue-500 hover:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:ring-blue-800 dark:disabled:border-gray-600 dark:disabled:text-gray-500"
  };

  // Size variants for different button sizes
  const sizes = {
    /** Small size - Compact buttons */
    sm: "text-xs px-3 py-1.5",
    
    /** Medium size - Default button size */
    md: "text-sm px-5 py-2.5",
    
    /** Large size - Prominent buttons */
    lg: "text-base px-6 py-3"
  };

  return (
    <button
      // Combine all style classes
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      // Disable button during loading or when disabled prop is true
      disabled={isLoading || disabled}
      {...props}
    >
      {/* Show loading spinner or left icon */}
      {isLoading ? (
        // Loading spinner SVG
        <svg 
          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : leftIcon ? (
        // Left icon with right margin
        <span className="mr-2">{leftIcon}</span>
      ) : null}

      {/* Button text/children */}
      {children}

      {/* Right icon - only shown when not loading */}
      {rightIcon && !isLoading && (
        <span className="ml-2">{rightIcon}</span>
      )}
    </button>
  );
};

export default CustomButton; 